import React, { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

import { toast } from "sonner";
import { Download, FileText, FileSpreadsheet, Calendar } from "lucide-react";
import { CATEGORY_LABELS, formatAmount } from "@shared/types";
import type { TransactionCategory } from "@shared/types";
import { useLanguage, CATEGORY_LABELS_AR } from "@/contexts/LanguageContext";
import { useAuth } from "@/_core/hooks/useAuth";
import { RouteFeedback } from "@/components/RouteFeedback";

// ─── Period helpers ───────────────────────────────────────────────────────────

function getMonthRange(year: number, month: number) {
  const start = new Date(year, month, 1).getTime();
  const end = new Date(year, month + 1, 0, 23, 59, 59, 999).getTime();
  return { start, end };
}

function getQuarterRange(year: number, quarter: number) {
  const startMonth = (quarter - 1) * 3;
  const start = new Date(year, startMonth, 1).getTime();
  const end = new Date(year, startMonth + 3, 0, 23, 59, 59, 999).getTime();
  return { start, end };
}

function getYearRange(year: number) {
  const start = new Date(year, 0, 1).getTime();
  const end = new Date(year, 11, 31, 23, 59, 59, 999).getTime();
  return { start, end };
}

function getCurrentQuarter(month: number) {
  return Math.floor(month / 3) + 1;
}

function formatPeriodEnd(endDate: number, language: string) {
  const d = new Date(endDate);
  return d.toLocaleDateString(language === "ar" ? "ar-KW" : "en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Number formatting ────────────────────────────────────────────────────────

function fmtNum(n: number): string {
  if (n === 0) return "—";
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  return n < 0 ? `(${formatted})` : formatted;
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function ExportPage() {
  const { t, language, isRTL } = useLanguage();
  const { user } = useAuth();
  // Extended labels for categories stored in DB that don't match the standard CATEGORY_LABELS keys
  const EXTRA_LABELS_EN: Record<string, string> = {
    bills_utilities: "Bills & Utilities",
    fees_charges: "Fees & Charges",
    atm_withdrawal: "ATM Withdrawal",
    credit_card_payment: "Credit Card Payment",
    transfer: "Transfer",
    other: "Other",
  };
  const EXTRA_LABELS_AR: Record<string, string> = {
    bills_utilities: "فواتير وخدمات",
    fees_charges: "رسوم ومصاريف",
    atm_withdrawal: "سحب صراف آلي",
    credit_card_payment: "دفع بطاقة ائتمان",
    transfer: "تحويل",
    other: "أخرى",
  };
  const getCategoryLabel = (cat: string) => {
    const c = cat === "bills_utilities" ? "bills" : cat;
    if (language === "ar") {
      return EXTRA_LABELS_AR[c] ?? CATEGORY_LABELS_AR[c] ?? CATEGORY_LABELS[c as TransactionCategory] ?? c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
    }
    return EXTRA_LABELS_EN[c] ?? CATEGORY_LABELS[c as TransactionCategory] ?? c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  const now = useMemo(() => new Date(), []);
  const [periodType, setPeriodType] = useState<"monthly" | "quarterly" | "yearly">("monthly");
  const [selectedMonth, setSelectedMonth] = useState(() => `${now.getFullYear()}-${now.getMonth()}`);
  const [selectedQuarter, setSelectedQuarter] = useState(() => `${now.getFullYear()}-${getCurrentQuarter(now.getMonth())}`);
  const [selectedYear, setSelectedYear] = useState(() => `${now.getFullYear()}`);
  const [exporting, setExporting] = useState(false);
  const { isLoading: workspaceExportLoading, refetch: fetchWorkspaceExport } = trpc.account.exportWorkspaceData.useQuery(undefined, { enabled: false });

  const exportWorkspaceJSON = async () => {
    try {
      const result = await fetchWorkspaceExport();
      if (result.error || !result.data) {
        toast.error(t("export.workspaceExportUnavailable"));
        return;
      }
      const blob = new Blob([JSON.stringify(result.data.json, null, 2)], { type: "application/json" });
      downloadBlob(blob, `mizano-workspace-data-${new Date().toISOString().split("T")[0]}.json`);
      toast.success(t("export.workspaceExportReady"));
    } catch {
      toast.error(t("export.workspaceExportUnavailable"));
    }
  };


  // Compute date range based on period type
  const { start: startDate, end: endDate } = useMemo(() => {
    if (periodType === "monthly") {
      const [y, m] = selectedMonth.split("-").map(Number);
      return getMonthRange(y, m);
    } else if (periodType === "quarterly") {
      const [y, q] = selectedQuarter.split("-").map(Number);
      return getQuarterRange(y, q);
    } else {
      const y = parseInt(selectedYear);
      return getYearRange(y);
    }
  }, [periodType, selectedMonth, selectedQuarter, selectedYear]);

  // Data queries
  const summaryQuery = trpc.transaction.summary.useQuery({ startDate, endDate });
  const incomeCatsQuery = trpc.transaction.categoryBreakdown.useQuery({ type: "cash_in", startDate, endDate });
  const expenseCatsQuery = trpc.transaction.categoryBreakdown.useQuery({ type: "cash_out", startDate, endDate });
  const balanceQuery = trpc.transaction.latestBalance.useQuery();
  const cashFlowQuery = trpc.transaction.cashFlow.useQuery({ startDate, endDate });
  const transactionsQuery = trpc.transaction.list.useQuery({ startDate, endDate, limit: 200, offset: 0 });
  const { data: summary } = summaryQuery;
  const { data: incomeCats } = incomeCatsQuery;
  const { data: expenseCats } = expenseCatsQuery;
  const { data: balance } = balanceQuery;
  const { data: cashFlow } = cashFlowQuery;
  const { data: transactions } = transactionsQuery;
  const pageQueries = [summaryQuery, incomeCatsQuery, expenseCatsQuery, balanceQuery, cashFlowQuery, transactionsQuery];
  const isPageLoading = pageQueries.some((query) => query.isLoading);
  const isPageError = pageQueries.some((query) => query.isError);
  const retryPageQueries = () => { pageQueries.forEach((query) => { void query.refetch(); }); };

  const cashIn = summary?.find((s) => s.type === "cash_in");
  const cashOut = summary?.find((s) => s.type === "cash_out");
  const totalIn = parseFloat(cashIn?.total ?? "0");
  const totalOut = parseFloat(cashOut?.total ?? "0");
  const netIncome = totalIn - totalOut;
  const txCount = (parseInt(String(cashIn?.count ?? "0")) + parseInt(String(cashOut?.count ?? "0")));
  const currentBalance = balance != null ? parseFloat(String(balance)) : null;

  // Cash flow statement values
  const openingBalance = cashFlow?.openingBalance ?? null;
  const closingBalance = cashFlow?.closingBalance ?? null;
  const netMovement = netIncome; // Net movement = cash in - cash out from income statement

  // Period label for display
  const periodLabel = useMemo(() => {
    if (periodType === "monthly") {
      const [y, m] = selectedMonth.split("-").map(Number);
      return new Date(y, m).toLocaleString(language === "ar" ? "ar-KW" : "en-US", { month: "long", year: "numeric" });
    } else if (periodType === "quarterly") {
      const [y, q] = selectedQuarter.split("-").map(Number);
      const qLabel = language === "ar" ? `الربع ${q}` : `Q${q}`;
      return `${qLabel} ${y}`;
    } else {
      return selectedYear;
    }
  }, [periodType, selectedMonth, selectedQuarter, selectedYear, language]);

  // Period options
  const monthOptions = useMemo(() => {
    const opts = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      opts.push({
        value: `${d.getFullYear()}-${d.getMonth()}`,
        label: d.toLocaleString(language === "ar" ? "ar-KW" : "en-US", { month: "long", year: "numeric" }),
      });
    }
    return opts;
  }, [now, language]);

  const quarterOptions = useMemo(() => {
    const opts = [];
    const curQ = getCurrentQuarter(now.getMonth());
    for (let i = 0; i < 8; i++) {
      let q = curQ - (i % 4);
      let y = now.getFullYear() - Math.floor(i / 4);
      if (q <= 0) { q += 4; y -= 1; }
      const qLabel = language === "ar" ? `الربع ${q}` : `Q${q}`;
      opts.push({ value: `${y}-${q}`, label: `${qLabel} ${y}` });
    }
    return opts;
  }, [now, language]);

  const yearOptions = useMemo(() => {
    const opts = [];
    for (let i = 0; i < 5; i++) {
      const y = now.getFullYear() - i;
      opts.push({ value: `${y}`, label: `${y}` });
    }
    return opts;
  }, [now]);

  // ─── Cash flow summary paragraph ─────────────────────────────────────────────

  const cashFlowParagraph = useMemo(() => {
    if (txCount === 0) return "";

    const topExpenses = expenseCats?.slice(0, 3).map((c) => getCategoryLabel(c.category ?? "uncategorized")) ?? [];
    const topIncome = incomeCats?.slice(0, 2).map((c) => getCategoryLabel(c.category ?? "uncategorized")) ?? [];
    const expenseRatio = totalIn > 0 ? ((totalOut / totalIn) * 100).toFixed(0) : "N/A";
    const savingsRate = totalIn > 0 ? (((totalIn - totalOut) / totalIn) * 100).toFixed(0) : "N/A";

    if (language === "ar") {
      const status = netIncome >= 0 ? "فائض" : "عجز";
      return `خلال فترة ${periodLabel}، بلغ إجمالي الإيرادات ${fmtNum(totalIn)} د.ك من ${txCount} معاملة. ` +
        (topIncome.length > 0 ? `تمثلت مصادر الدخل الرئيسية في ${topIncome.join(" و")}. ` : "") +
        `بلغ إجمالي المصروفات التشغيلية ${fmtNum(totalOut)} د.ك` +
        (topExpenses.length > 0 ? `، حيث شكلت ${topExpenses.join(" و")} أكبر فئات الإنفاق` : "") +
        `. نتج عن ذلك ${status} صافي قدره ${fmtNum(Math.abs(netIncome))} د.ك. ` +
        (totalIn > 0 ? `بلغت نسبة المصروفات إلى الإيرادات ${expenseRatio}% مع معدل ادخار ${savingsRate}%.` : "");
    }

    const status = netIncome >= 0 ? "a surplus" : "a deficit";
    return `During the ${periodLabel} period, total revenue amounted to KWD ${fmtNum(totalIn)} across ${txCount} transactions. ` +
      (topIncome.length > 0 ? `Primary income sources included ${topIncome.join(" and ")}. ` : "") +
      `Total operating expenses were KWD ${fmtNum(totalOut)}` +
      (topExpenses.length > 0 ? `, with ${topExpenses.join(", ")} representing the largest expense categories` : "") +
      `. This resulted in ${status} of KWD ${fmtNum(Math.abs(netIncome))}. ` +
      (totalIn > 0 ? `The expense-to-revenue ratio stood at ${expenseRatio}%, yielding a savings rate of ${savingsRate}%.` : "");
  }, [txCount, totalIn, totalOut, netIncome, periodLabel, incomeCats, expenseCats, language]);

  // ─── PDF Export ───────────────────────────────────────────────────────────────

  const exportPDF = () => {
    if (txCount === 0) {
      toast.error(t("export.noData"));
      return;
    }
    setExporting(true);
    try {
      const userName = user?.name ?? "Account Holder";
      const periodEndFormatted = formatPeriodEnd(endDate, language);

      const revenueRows = (incomeCats ?? []).map((c) => `
        <tr>
          <td class="line-item">${getCategoryLabel(c.category ?? "uncategorized")}</td>
          <td class="amount">${fmtNum(parseFloat(c.total ?? "0"))}</td>
        </tr>
      `).join("");

      const expenseRows = (expenseCats ?? []).map((c) => `
        <tr>
          <td class="line-item">${getCategoryLabel(c.category ?? "uncategorized")}</td>
          <td class="amount">${fmtNum(parseFloat(c.total ?? "0"))}</td>
        </tr>
      `).join("");

      const html = `<!DOCTYPE html>
<html dir="${isRTL ? "rtl" : "ltr"}">
<head>
  <meta charset="UTF-8">
  <title>Statement of Income - ${periodLabel}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Inter', -apple-system, sans-serif;
      color: #1a1a2e;
      background: #fff;
      padding: 48px 56px;
      font-size: 13px;
      line-height: 1.6;
    }
    .header {
      border-bottom: 3px solid #1a1a2e;
      padding-bottom: 20px;
      margin-bottom: 8px;
    }
    .header h1 {
      font-size: 15px;
      font-weight: 700;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      margin-bottom: 2px;
    }
    .header .entity {
      font-size: 22px;
      font-weight: 700;
      color: #1a1a2e;
      margin-bottom: 4px;
    }
    .header .subtitle {
      font-size: 14px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      color: #333;
    }
    .header .period {
      font-size: 12px;
      color: #666;
      margin-top: 2px;
    }
    .header .unaudited {
      font-size: 11px;
      color: #888;
      font-style: italic;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 16px 0;
    }
    th {
      text-align: ${isRTL ? "right" : "left"};
      font-size: 11px;
      font-weight: 600;
      color: #666;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 8px 0;
      border-bottom: 1px solid #ddd;
    }
    th.amount-header {
      text-align: ${isRTL ? "left" : "right"};
      width: 160px;
    }
    td {
      padding: 6px 0;
      vertical-align: top;
    }
    td.line-item {
      padding-${isRTL ? "right" : "left"}: 24px;
      color: #333;
    }
    td.amount {
      text-align: ${isRTL ? "left" : "right"};
      font-variant-numeric: tabular-nums;
      font-weight: 500;
      width: 160px;
    }
    .section-header td {
      font-weight: 700;
      color: #1a1a2e;
      padding-top: 16px;
      padding-bottom: 4px;
      font-size: 13px;
    }
    .subtotal td {
      font-weight: 700;
      color: #1a1a2e;
      border-top: 1px solid #333;
      padding-top: 8px;
      padding-bottom: 8px;
    }
    .subtotal td.amount {
      font-weight: 700;
    }
    .net-total td {
      font-weight: 700;
      font-size: 14px;
      color: #1a1a2e;
      border-top: 2px solid #1a1a2e;
      border-bottom: 3px double #1a1a2e;
      padding-top: 10px;
      padding-bottom: 10px;
    }
    .separator td {
      padding: 4px 0;
    }
    .separator td hr {
      border: none;
      border-top: 1px solid #ddd;
    }
    .cash-flow-statement {
      page-break-before: always;
      margin-top: 0;
      padding-top: 24px;
      border-top: none;
    }
    .cash-flow-statement h2 {
      font-size: 14px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      color: #333;
      margin-bottom: 4px;
    }
    .cash-flow-statement .cf-period {
      font-size: 12px;
      color: #666;
      margin-bottom: 16px;
    }
    .cash-flow-section {
      margin-top: 24px;
      padding: 20px 24px;
      background: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 4px;
    }
    .cash-flow-section h3 {
      font-size: 13px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      color: #1a1a2e;
      margin-bottom: 12px;
    }
    .cash-flow-section p {
      font-size: 12px;
      line-height: 1.8;
      color: #444;
      text-align: justify;
    }
    .footer {
      margin-top: 40px;
      padding-top: 16px;
      border-top: 1px solid #ddd;
      font-size: 10px;
      color: #999;
      display: flex;
      justify-content: space-between;
    }
    .currency-note {
      text-align: ${isRTL ? "left" : "right"};
      font-size: 11px;
      color: #888;
      margin-bottom: 8px;
    }
    .toolbar {
      position: sticky;
      top: 0;
      z-index: 100;
      background: #1a1a2e;
      color: #fff;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 24px;
      margin: -48px -56px 24px -56px;
      font-family: 'Inter', -apple-system, sans-serif;
    }
    .toolbar button {
      background: rgba(255,255,255,0.15);
      color: #fff;
      border: 1px solid rgba(255,255,255,0.25);
      padding: 8px 20px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      font-family: inherit;
    }
    .toolbar button:active {
      background: rgba(255,255,255,0.3);
    }
    @media print {
      body { padding: 24px 32px; }
      .toolbar { display: none !important; }
      .cash-flow-statement { page-break-before: always; }
      .cash-flow-section { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <button onclick="window.close(); if(!window.closed) history.back();">✕ ${language === "ar" ? "إغلاق" : "Close"}</button>
    <button onclick="window.print();">⬇ ${language === "ar" ? "حفظ PDF" : "Save PDF"}</button>
  </div>
  <div class="header">
    <div class="entity">${userName}</div>
    <div class="subtitle">STATEMENT OF INCOME</div>
    <div class="period">${periodLabel} &mdash; Period Ended ${periodEndFormatted}</div>
    <div class="unaudited">(Personal &middot; Unaudited)</div>
  </div>

  <div class="currency-note">Amounts in KWD</div>
  <table>
    <thead>
      <tr>
        <th></th>
        <th class="amount-header">${periodLabel}</th>
      </tr>
    </thead>
    <tbody>
      <!-- Cash In Section -->
      <tr class="section-header">
        <td>Cash In</td>
        <td></td>
      </tr>
      ${revenueRows || '<tr><td class="line-item" style="color:#999;font-style:italic;">No cash in recorded</td><td class="amount">—</td></tr>'}
      <tr class="separator"><td colspan="2"><hr/></td></tr>
      <tr class="subtotal">
        <td>Total Cash In</td>
        <td class="amount">${fmtNum(totalIn)}</td>
      </tr>

      <!-- Cash Out Section -->
      <tr class="section-header">
        <td>Cash Out</td>
        <td></td>
      </tr>
      ${expenseRows || '<tr><td class="line-item" style="color:#999;font-style:italic;">No cash out recorded</td><td class="amount">—</td></tr>'}
      <tr class="separator"><td colspan="2"><hr/></td></tr>
      <tr class="subtotal">
        <td>Total Cash Out</td>
        <td class="amount">${fmtNum(totalOut)}</td>
      </tr>

      <!-- Net Cash Flow -->
      <tr class="section-header"><td colspan="2"></td></tr>
      <tr class="net-total">
        <td>Net Cash Flow</td>
        <td class="amount">${fmtNum(netIncome)}</td>
      </tr>
    </tbody>
  </table>

  <!-- Cash Flow Statement -->
  <div class="cash-flow-statement">
    <h2>STATEMENT OF CASH FLOWS</h2>
    <div class="cf-period">${periodLabel} &mdash; Period Ended ${periodEndFormatted}</div>

    <table>
      <thead>
        <tr>
          <th></th>
          <th class="amount-header">${periodLabel}</th>
        </tr>
      </thead>
      <tbody>
        <tr class="section-header">
          <td>Cash Balance</td>
          <td></td>
        </tr>
        <tr>
          <td class="line-item">Opening Balance</td>
          <td class="amount">${openingBalance !== null ? fmtNum(openingBalance) : "N/A"}</td>
        </tr>

        <tr class="section-header">
          <td>Cash Movements During Period</td>
          <td></td>
        </tr>
        <tr>
          <td class="line-item">Total Cash In</td>
          <td class="amount" style="color:#16a34a;">${fmtNum(totalIn)}</td>
        </tr>
        <tr>
          <td class="line-item">Total Cash Out</td>
          <td class="amount" style="color:#dc2626;">(${fmtNum(totalOut)})
        </tr>
        <tr class="separator"><td colspan="2"><hr/></td></tr>
        <tr class="subtotal">
          <td>Net Movement</td>
          <td class="amount" style="color:${netMovement >= 0 ? '#16a34a' : '#dc2626'};">${netMovement >= 0 ? '+' : ''}${fmtNum(netMovement)}</td>
        </tr>

        <tr class="section-header"><td colspan="2"></td></tr>
        <tr class="net-total">
          <td>Closing Balance</td>
          <td class="amount">${closingBalance !== null ? fmtNum(closingBalance) : "N/A"}</td>
        </tr>
      </tbody>
    </table>
  </div>

  ${cashFlowParagraph ? `
  <div class="cash-flow-section">
    <h3>Notes</h3>
    <p>${cashFlowParagraph}</p>
  </div>
  ` : ""}

  <div class="footer">
    <span>Generated by Mizano</span>
    <span>${new Date().toLocaleDateString(language === "ar" ? "ar-KW" : "en-US", { day: "numeric", month: "long", year: "numeric" })}</span>
  </div>
</body>
</html>`;

      // Open in a new window for native Save as PDF / Share
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error(language === "ar" ? "يرجى السماح بالنوافذ المنبثقة" : "Please allow popups to export PDF");
        return;
      }
      printWindow.document.write(html);
      printWindow.document.close();
      toast.success(language === "ar" ? "تم فتح التقرير - استخدم مشاركة أو حفظ كـ PDF" : "Report opened — use Share or Save as PDF");
    } catch {
      toast.error(language === "ar" ? "فشل التصدير" : "Failed to export");
    } finally {
      setExporting(false);
    }
  };

  // ─── CSV Export ───────────────────────────────────────────────────────────────

  const exportCSV = () => {
    if (!transactions || transactions.length === 0) {
      toast.error(t("export.noData"));
      return;
    }
    setExporting(true);
    try {
      const headers = ["Date", "Type", "Currency", "Amount", "Amount (KWD)", "Merchant", "Category", "Balance", "Account"];
      const rows = transactions.map((tx) => [
        new Date(tx.transactionDate).toLocaleDateString(),
        tx.type === "cash_in" ? "Cash In" : "Cash Out",
        tx.currency ?? "KWD",
        parseFloat(tx.amount).toFixed(3),
        tx.amountInKwd ? parseFloat(tx.amountInKwd).toFixed(3) : parseFloat(tx.amount).toFixed(3),
        tx.merchant ?? "Unknown",
        CATEGORY_LABELS[tx.category as TransactionCategory] ?? tx.category,
        tx.balance ? parseFloat(tx.balance).toFixed(3) : "",
        tx.accountNumber ?? "",
      ]);

      // Summary section
      rows.push([]);
      rows.push(["STATEMENT OF INCOME SUMMARY"]);
      rows.push(["", "Total Cash In", "KWD", totalIn.toFixed(3)]);
      rows.push(["", "Total Cash Out", "KWD", totalOut.toFixed(3)]);
      rows.push(["", "Net Cash Flow", "KWD", netIncome.toFixed(3)]);
      // Category breakdown
      rows.push([]);
      rows.push(["CASH IN BREAKDOWN"]);
      (incomeCats ?? []).forEach((c) => {
        rows.push(["", CATEGORY_LABELS[c.category as TransactionCategory] ?? c.category, "KWD", parseFloat(c.total ?? "0").toFixed(3), "", "", "", "", `${c.count} transactions`]);
      });
      rows.push([]);
      rows.push(["CASH OUT BREAKDOWN"]);
      (expenseCats ?? []).forEach((c) => {
        rows.push(["", CATEGORY_LABELS[c.category as TransactionCategory] ?? c.category, "KWD", parseFloat(c.total ?? "0").toFixed(3), "", "", "", "", `${c.count} transactions`]);
      });

      // Cash Flow Statement
      rows.push([]);
      rows.push(["STATEMENT OF CASH FLOWS"]);
      rows.push(["", "Opening Balance", "KWD", openingBalance !== null ? openingBalance.toFixed(3) : "N/A"]);
      rows.push(["", "Total Cash Inflows", "KWD", totalIn.toFixed(3)]);
      rows.push(["", "Total Cash Outflows", "KWD", totalOut.toFixed(3)]);
      rows.push(["", "Net Movement", "KWD", netMovement.toFixed(3)]);
      rows.push(["", "Closing Balance", "KWD", closingBalance !== null ? closingBalance.toFixed(3) : "N/A"]);

      const csv = [headers.join(","), ...rows.map((r) => (r as string[]).join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      downloadBlob(blob, `income-statement-${periodLabel.replace(/\s/g, "-")}.csv`);
      toast.success(language === "ar" ? "تم تصدير CSV بنجاح" : "CSV exported successfully");
    } catch {
      toast.error(language === "ar" ? "فشل التصدير" : "Failed to export");
    } finally {
      setExporting(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  if (isPageLoading || isPageError) {
    return <div dir={isRTL ? "rtl" : "ltr"}><RouteFeedback isLoading={isPageLoading} isError={isPageError} onRetry={retryPageQueries} label={t("export.title")} /></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" dir={isRTL ? "rtl" : "ltr"}>
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">
            {t("export.title")}
          </h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">
            {t("export.subtitle")}
          </p>
          <p className="text-muted-foreground text-xs mt-1 max-w-2xl">
            {t("export.workspaceScopeHint")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportWorkspaceJSON} disabled={workspaceExportLoading} variant="outline" size="sm" className="gap-2">
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">{t("export.workspaceData")}</span>
            <span className="sm:hidden">JSON</span>
          </Button>
          <Button onClick={exportCSV} disabled={exporting || txCount === 0} variant="outline" size="sm" className="gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            <span className="hidden sm:inline">{t("export.downloadCsv")}</span>
            <span className="sm:hidden">CSV</span>
          </Button>
          <Button onClick={exportPDF} disabled={exporting || txCount === 0} size="sm" className="gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">{t("export.downloadPdf")}</span>
            <span className="sm:hidden">PDF</span>
          </Button>
        </div>
      </div>

      {/* Period Selection -- gradient like Budgets summary */}
      <div className="rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 p-4 sm:p-6">
          <Tabs value={periodType} onValueChange={(v) => setPeriodType(v as "monthly" | "quarterly" | "yearly")}>
            <TabsList className="w-full mb-4">
              <TabsTrigger value="monthly" className="flex-1">{t("export.monthly")}</TabsTrigger>
              <TabsTrigger value="quarterly" className="flex-1">{t("export.quarterly")}</TabsTrigger>
              <TabsTrigger value="yearly" className="flex-1">{t("export.yearly")}</TabsTrigger>
            </TabsList>

            <TabsContent value="monthly">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-full sm:w-[280px]">
                  <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </TabsContent>

            <TabsContent value="quarterly">
              <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
                <SelectTrigger className="w-full sm:w-[280px]">
                  <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {quarterOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </TabsContent>

            <TabsContent value="yearly">
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-full sm:w-[280px]">
                  <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </TabsContent>
          </Tabs>
      </div>

      {/* Export Summary */}
      {txCount === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground/40" />
            <h3 className="text-lg font-semibold text-foreground mb-1">{t("export.noData")}</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">{t("export.noDataDesc")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Quick Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">
                  {language === "ar" ? "الداخل" : "Cash In"}
                </p>
                <p className="text-sm sm:text-lg font-bold text-green-600 dark:text-green-400 tabular-nums mt-1">
                  {fmtNum(totalIn)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="p-4">
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">
                  {language === "ar" ? "الخارج" : "Cash Out"}
                </p>
                <p className="text-sm sm:text-lg font-bold text-red-600 dark:text-red-400 tabular-nums mt-1">
                  {fmtNum(totalOut)}
                </p>
              </CardContent>
            </Card>
            <Card className={`border-l-4 ${netIncome >= 0 ? "border-l-emerald-500" : "border-l-orange-500"}`}>
              <CardContent className="p-4">
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">
                  {language === "ar" ? "صافي التدفق" : "Net Cash Flow"}
                </p>
                <p className={`text-sm sm:text-lg font-bold tabular-nums mt-1 ${netIncome >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-orange-600 dark:text-orange-400"}`}>
                  {fmtNum(netIncome)}
                </p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">
                  {language === "ar" ? "المعاملات" : "Transactions"}
                </p>
                <p className="text-sm sm:text-lg font-bold text-blue-600 dark:text-blue-400 tabular-nums mt-1">
                  {txCount}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Cash Flow Preview */}
          {(openingBalance !== null || closingBalance !== null) && (
            <Card className="border-l-4 border-l-cyan-500">
              <CardContent className="p-4 sm:p-5">
                <p className="text-xs font-bold text-foreground uppercase tracking-wide mb-3">
                  {language === "ar" ? "ملخص التدفق النقدي" : "Cash Flow Overview"}
                </p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">
                      {language === "ar" ? "الرصيد الافتتاحي" : "Opening Balance"}
                    </p>
                    <p className="text-sm sm:text-base font-semibold tabular-nums text-foreground mt-0.5">
                      {openingBalance !== null ? fmtNum(openingBalance) : "--"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">
                      {language === "ar" ? "صافي الحركة" : "Net Movement"}
                    </p>
                    <p className={`text-sm sm:text-base font-semibold tabular-nums mt-0.5 ${netMovement >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                      {netMovement >= 0 ? "+" : ""}{fmtNum(netMovement)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">
                      {language === "ar" ? "الرصيد الختامي" : "Closing Balance"}
                    </p>
                    <p className="text-sm sm:text-base font-semibold tabular-nums text-foreground mt-0.5">
                      {closingBalance !== null ? fmtNum(closingBalance) : "--"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Export Prompt */}
          <Card className="bg-muted/30 border-dashed">
            <CardContent className="py-8 text-center">
              <Download className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-sm font-medium text-foreground mb-1">
                {language === "ar" ? "جاهز للتصدير" : "Ready to Export"}
              </p>
              <p className="text-xs text-muted-foreground max-w-md mx-auto mb-4">
                {language === "ar"
                  ? "يتضمن التقرير بيان الدخل وبيان التدفق النقدي مع الأرصدة الافتتاحية والختامية"
                  : "Your report includes the Income Statement and Cash Flow Statement with opening and closing balances"}
              </p>
              <div className="flex justify-center gap-3">
                <Button onClick={exportCSV} disabled={exporting} variant="outline" size="sm" className="gap-2">
                  <FileSpreadsheet className="h-4 w-4" />
                  CSV
                </Button>
                <Button onClick={exportPDF} disabled={exporting} size="sm" className="gap-2">
                  <FileText className="h-4 w-4" />
                  PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
